import { useRef } from 'react';
import type { Article } from '@/mocks/articles';

interface HotRankingSectionProps {
  articles: Article[];
}

export default function HotRankingSection({ articles }: HotRankingSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 320;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <section className="py-10 md:py-14 bg-background-100">
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
              吃瓜热榜
              <span className="text-primary-500 ml-2 text-sm font-sans font-medium">实时更新</span>
            </h2>
            <p className="text-xs text-foreground-500 mt-1.5">全网热议话题实时排名，热度数据每分钟刷新</p>
          </div>
          <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-foreground-900 text-background-50 text-xs font-semibold whitespace-nowrap">
            TOP 50
          </span>
        </div>

        <div className="relative group">
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background-50/90 text-foreground-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer border border-background-200"
            aria-label="向左滑动"
          >
            <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {articles.map((article, index) => (
              <a
                key={article.id}
                href={`/article/${article.slug}`}
                className="flex-shrink-0 w-[260px] sm:w-[280px] group/card cursor-pointer"
              >
                <div className="relative aspect-square rounded-lg overflow-hidden bg-background-200 mb-3">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title} - 911爆料网吃瓜热榜`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover/card:scale-105"
                    loading="lazy"
                  />
                  {article.isTop && (
                    <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded text-xs font-bold bg-primary-500/90 text-background-50 backdrop-blur-sm">
                      爆
                    </span>
                  )}
                  <span className="absolute bottom-2.5 right-2.5 text-4xl font-heading font-bold text-background-50/80">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <div className="px-0.5">
                  <span className="text-xs text-primary-500 font-medium">{article.category}</span>
                  <p className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug mt-1 group-hover/card:text-primary-500 transition-colors">
                    {article.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-accent-500 font-semibold">{article.heat} 热度</span>
                  </div>
                </div>
              </a>
            ))}
            <div className="flex-shrink-0 w-20"></div>
          </div>

          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-background-50/90 text-foreground-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer border border-background-200"
            aria-label="向右滑动"
          >
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      </div>
    </section>
  );
}